import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs/internal/Observable';
import { API_URLS } from '../constants/app-API.constants';
import {
  ApiResponse,
  ApplicationDetailsResponse,
  DocumentGenerationContentResponse,
  DocumentGenerationStatusResponse,
  DocumentGenerationVersionsResponse,
  GeneratedDocumentsResponse,
  RegenerateDocumentsRequest,
  RegenerateDocumentsResponse,
  SubmitDocumentGenerationRequest,
  SubmitDocumentGenerationResponse,
} from '../../features/_feature-models/document-generation.model';
import { ApplicationRepositoriesResponse } from '../../features/_feature-models/repository-details.model';
import { throwError } from 'rxjs';
import { ApplicationsResponse } from '../../features/_feature-models/applications.model';
import {
  ClusterDetailApiResponse,
  ClusterMatricesApiResponse,
} from '../../features/_feature-models/cluster-details.model';
import { ClusterApiResponse } from '../../features/_feature-models/discover-tab.model';
import { HttpUtilityService } from './app-http.utility.service';
import {
  ComparisonMatrixReportApiResponse,
  ComparisonMatrixStatusApiResponse,
  SubmitComparisonMatrixApiResponse,
  SubmitComparisonMatrixRequest,
} from '../../features/_feature-models/comparison-matrix.model';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private readonly httpUtilityService = inject(HttpUtilityService);

  getQuotaDetails(): Observable<any> {
    return this.httpUtilityService.get(API_URLS.copilotQuota);
  }

  getApplicationDetails(appId: string): Observable<ApiResponse<ApplicationDetailsResponse>> {
    return this.httpUtilityService.get<ApiResponse<ApplicationDetailsResponse>>(
      API_URLS.applicationDetails(appId),
      undefined,
      0,
    );
  }

  getApplicationRepositories(
    appId: string,
  ): Observable<ApiResponse<ApplicationRepositoriesResponse>> {
    return this.httpUtilityService.get<ApiResponse<ApplicationRepositoriesResponse>>(
      API_URLS.applicationRepositories(appId),
      undefined,
      0,
      false,
      true,
    );
  }

  getGeneratedDocuments(generationId: string): Observable<ApiResponse<GeneratedDocumentsResponse>> {
    return this.httpUtilityService.get<ApiResponse<GeneratedDocumentsResponse>>(
      API_URLS.generatedDocuments(generationId),
      undefined,
      0,
    );
  }

  getDocumentGenerationVersions(
    appId: string,
  ): Observable<ApiResponse<DocumentGenerationVersionsResponse>> {
    return this.httpUtilityService.get<ApiResponse<DocumentGenerationVersionsResponse>>(
      API_URLS.documentGenerationVersions(appId),
      undefined,
      0,
    );
  }

  submitDocumentGeneration(
    request: SubmitDocumentGenerationRequest,
  ): Observable<ApiResponse<SubmitDocumentGenerationResponse>> {
    return this.httpUtilityService.post<ApiResponse<SubmitDocumentGenerationResponse>>(
      API_URLS.submitDocumentGeneration,
      request,
      0,
      true,
    );
  }

  submitComparisonMatrix(
    request: SubmitComparisonMatrixRequest,
  ): Observable<SubmitComparisonMatrixApiResponse> {
    return this.httpUtilityService.post<SubmitComparisonMatrixApiResponse>(
      API_URLS.submitComparisonMatrix,
      request,
      0,
      true,
    );
  }

  getComparisonMatrixStatus(runId: string): Observable<ComparisonMatrixStatusApiResponse> {
    return this.httpUtilityService.get<ComparisonMatrixStatusApiResponse>(
      API_URLS.comparisonMatrixStatus(runId),
      undefined,
      0,
      true,
    );
  }

  getComparisonMatrixReport(runId: string): Observable<ComparisonMatrixReportApiResponse> {
    return this.httpUtilityService.get<ComparisonMatrixReportApiResponse>(
      API_URLS.comparisonMatrixReport(runId),
      undefined,
      0,
      true,
    );
  }

  downloadComparisonMatrixReports(runId: string): Observable<HttpResponse<Blob>> {
    return this.httpUtilityService.getBlob(API_URLS.downloadComparisonMatrixReports(runId), true);
  }

  getDocumentGenerationStatus(
    generationId: string,
  ): Observable<ApiResponse<DocumentGenerationStatusResponse>> {
    return this.httpUtilityService.get<ApiResponse<DocumentGenerationStatusResponse>>(
      API_URLS.documentGenerationStatus(generationId),
      undefined,
      0,
      true,
    );
  }

  getDocumentGenerationContent(
    generationId: string,
  ): Observable<ApiResponse<DocumentGenerationContentResponse>> {
    return this.httpUtilityService.get<ApiResponse<DocumentGenerationContentResponse>>(
      API_URLS.documentGenerationContent(generationId),
      undefined,
      0,
      true,
    );
  }

  regenerateDocuments(
    generationId: string,
    request: RegenerateDocumentsRequest,
  ): Observable<ApiResponse<RegenerateDocumentsResponse>> {
    return this.httpUtilityService.post<ApiResponse<RegenerateDocumentsResponse>>(
      API_URLS.regenerateDocuments(generationId),
      request,
      0,
      true,
    );
  }

  downloadDocumentReports(generationId: string): Observable<HttpResponse<Blob>> {
    return this.httpUtilityService.getBlob(API_URLS.downloadDocumentReports(generationId), true);
  }

  getApplications(
    pageNumber: number,
    pageSize: number,
    search = '',
  ): Observable<ApplicationsResponse> {
    return this.httpUtilityService.get<ApplicationsResponse>(
      '/api/applications',
      this.optionalParams(pageNumber, pageSize, search),
      0,
      false,
      true,
    );
  }

  getClusterDetails(
    pageNumber: number,
    pageSize: number,
    search = '',
  ): Observable<ClusterApiResponse> {
    return this.httpUtilityService.get<ClusterApiResponse>(
      '/api/clusters',
      this.optionalParams(pageNumber, pageSize, search),
      0,
      false,
      true,
    );
  }

  /** One cluster with a page of its applications, optionally searched by ID or name. */
  getClusterByID(
    clusterId: string,
    pageNumber: number,
    pageSize: number,
    search?: string,
  ): Observable<ClusterDetailApiResponse> {
    if (!ApiService.isClusterId(clusterId)) {
      return throwError(() => new Error('Invalid cluster id'));
    }
    return this.httpUtilityService.get<ClusterDetailApiResponse>(
      `/api/clusters/${clusterId}`,
      this.optionalParams(pageNumber, pageSize, search),
      0,
      false,
      true,
    );
  }

  /** A page of the matrices of one cluster. `search` is sent only when set. */
  getClusterMatrices(
    clusterId: string,
    pageNumber: number,
    pageSize: number,
    search?: string,
  ): Observable<ClusterMatricesApiResponse> {
    if (!ApiService.isClusterId(clusterId)) {
      return throwError(() => new Error('Invalid cluster id'));
    }
    return this.httpUtilityService.get<ClusterMatricesApiResponse>(
      `/api/clusters/${clusterId}/generated-matrices`,
      this.optionalParams(pageNumber, pageSize, search),
      0,
      false,
      true,
    );
  }

  /** Cluster identifiers fit Int32; -1 selects Unclustered. */
  static isClusterId(value: string): boolean {
    return value === '-1' || (/^\d+$/.test(value) && Number(value) <= 2147483647);
  }

  /** Always sends the page parameters; sends search only when it has a value. */
  private optionalParams(
    pageNumber: number,
    pageSize: number,
    search?: string,
  ): HttpParams {
    let params = new HttpParams().set('pageNumber', pageNumber).set('pageSize', pageSize);
    const term = search?.trim();
    if (term) {
      params = params.set('search', term);
    }
    return params;
  }
}
